import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Download, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Section, SectionHead, FAQ, Breadcrumbs } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { SectionLabel, Disclaimer } from "@/components/ui-kit";
import { Control, Chart } from "@/components/Calculators";
import GateForm from "@/components/GateForm";
import { getGoal, GOALS } from "@/constants/goals";
import { glideAllocation, fmtINR } from "@/lib/finance";
import { downloadGoalReport } from "@/lib/goalReport";
import NotFound from "@/pages/NotFound";

const NAVY = "#112C50";
const ORANGE = "#F1771E";

const btn =
  "inline-flex items-center gap-2 px-8 py-4 font-sans text-sm font-medium transition-[background-color,color,box-shadow,transform] duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-citrine focus-visible:ring-offset-2 active:translate-y-px";

export default function GoalPage() {
  const { slug } = useParams();
  const goal = getGoal(slug);
  if (!goal) return <NotFound />;
  return <GoalLanding key={goal.slug} goal={goal} />;
}

function GoalLanding({ goal }) {
  const [values, setValues] = useState(() => Object.fromEntries(goal.inputs.map((i) => [i.key, i.default])));
  const result = useMemo(() => goal.compute(values), [goal, values]);
  const [reportUnlocked, setReportUnlocked] = useState(false);
  const [ebookUnlocked, setEbookUnlocked] = useState(false);
  const [leadName, setLeadName] = useState("");

  const deliverReport = async (lead) => {
    setLeadName(lead.name);
    setReportUnlocked(true);
    await downloadGoalReport({ goal, values, result, name: lead.name });
  };
  const deliverEbook = () => {
    setEbookUnlocked(true);
    window.open(goal.ebook.file, "_blank", "noopener");
  };

  const alloc = result.allocation || glideAllocation(result.yearsLeft);
  // Goals may supply their own comparison; otherwise show the cost of waiting.
  const compare =
    result.compare ||
    (result.delay && {
      title: "The cost of waiting",
      intro: "Monthly SIP needed to reach the same amount if you start later.",
      rows: result.delay.map((d) => ({ label: d.delay === 0 ? "Start today" : `Start in ${d.delay} yr${d.delay > 1 ? "s" : ""}`, amount: d.sip, highlight: d.delay === 0 })),
    });
  const maxCompare = compare ? Math.max(...compare.rows.map((r) => r.amount), 1) : 1;
  const chartLabels = result.chartLabels || { value: "Estimated value", invested: "Invested", title: "How your plan may grow" };
  // Same-audience goals first, capped so the section stays short.
  const sameFirst = (g) => (g.audience === goal.audience ? 0 : 1);
  const others = GOALS.filter((g) => g.slug !== goal.slug)
    .sort((a, b) => sameFirst(a) - sameFirst(b))
    .slice(0, 6);

  return (
    <div>
      <SEO title={goal.seo.title} description={goal.seo.description} path={`/goals/${goal.slug}`} faq={goal.faq} />

      {/* HERO */}
      <section className="relative z-10 pt-36 pb-24 md:pt-44 md:pb-32 bg-cream overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_85%_15%,rgba(228,181,60,0.18),transparent_55%),radial-gradient(ellipse_at_5%_95%,rgba(17,44,80,0.08),transparent_50%)]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Goals", to: "/goals" }, { name: goal.name }]} />
            <Reveal>
              <div className="mt-8"><SectionLabel>{goal.hero.label}</SectionLabel></div>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-[-0.03em] text-navy">
                {goal.hero.title}
                <span className="block text-orange">{goal.hero.accent}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 max-w-xl font-sans text-lg leading-[1.7] text-slate">{goal.hero.intro}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href="#calculator" className={`${btn} bg-orange text-white shadow-elevated hover:bg-orange-dark hover:shadow-citrine`} data-testid="goal-hero-calc">
                  Calculate my plan
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </a>
                <a href="#guide" className={`${btn} border border-navy text-navy hover:bg-navy hover:text-white`} data-testid="goal-hero-guide">
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                  Free guide
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5 flex justify-center">
            <EbookCover goal={goal} />
          </Reveal>
        </div>
      </section>

      {/* CALCULATOR */}
      <Section id="calculator">
        <SectionHead label="Goal planner" title={`Plan your ${goal.name.toLowerCase()} in a minute`} intro="Move the sliders. Your headline number updates instantly; unlock the full report for the detail." />
        <div className="grid lg:grid-cols-2 border border-navy/10 bg-white shadow-elevated">
          <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-navy/10">
            {goal.inputs.map((inp) => (
              <Control
                key={inp.key}
                label={inp.label}
                value={values[inp.key]}
                min={inp.min}
                max={inp.max}
                step={inp.step}
                format={inp.format}
                onChange={(v) => setValues((s) => ({ ...s, [inp.key]: v }))}
                testid={`goal-input-${inp.key}`}
              />
            ))}
          </div>
          <div className="p-8 lg:p-10 bg-navy-deep text-white flex flex-col gap-8">
            <div>
              <div className="font-sans text-xs uppercase tracking-[0.18em] text-citrine mb-2">{result.headline.label}</div>
              <div className="font-data text-4xl md:text-5xl" data-testid="goal-headline">{result.headline.value}</div>
            </div>

            <div className="relative">
              <div className={`grid grid-cols-2 gap-6 transition-[filter,opacity] duration-500 ${reportUnlocked ? "" : "blur-[6px] opacity-60 select-none"}`} aria-hidden={!reportUnlocked}>
                {result.stats.map((s) => (
                  <div key={s.label} className="border-t border-citrine/40 pt-3">
                    <div className="font-sans text-[11px] uppercase tracking-[0.16em] text-white/60">{s.label}</div>
                    <div className="font-data text-xl mt-1">{s.value}</div>
                  </div>
                ))}
              </div>
              {!reportUnlocked && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-sans text-xs uppercase tracking-[0.18em] bg-navy/90 border border-citrine/50 px-4 py-2">Full breakdown in your report</span>
                </div>
              )}
            </div>

            {reportUnlocked ? (
              <div className="flex flex-col gap-4" data-testid="goal-report-ready">
                <div className="flex items-center gap-3 text-citrine"><CheckCircle2 className="w-5 h-5" strokeWidth={1.5} /><span className="font-sans text-sm">Your report has downloaded. A copy is on its way to your email.</span></div>
                <button type="button" onClick={() => downloadGoalReport({ goal, values, result, name: leadName })} className={`${btn} self-start border border-citrine/60 text-white hover:bg-white hover:text-navy`} data-testid="goal-report-again">
                  <Download className="w-4 h-4" strokeWidth={1.5} /> Download again
                </button>
              </div>
            ) : (
              <div>
                <div className="font-serif text-2xl mb-1">Get your personalised report</div>
                <p className="font-sans text-sm text-white/65 mb-5">Year-by-year projection, suggested asset mix and next steps, as a PDF, downloaded now and emailed to you.</p>
                <GateForm goal={goal} magnet="Personalised report" result={result} cta="Unlock my report" onUnlocked={deliverReport} dark testid="report-gate" />
              </div>
            )}
          </div>
        </div>

        {(result.series?.length > 0 || compare) && (
          <div className={`mt-10 grid gap-6 ${result.series?.length > 0 && compare ? "lg:grid-cols-2" : ""}`}>
            {result.series?.length > 0 && (
              <div className="border border-navy/10 bg-white p-8 shadow-elevated">
                <div className="font-serif text-xl text-navy">{chartLabels.title}</div>
                <Chart data={result.series} keys={[{ key: "value", name: chartLabels.value, color: ORANGE, fill: "url(#gOrange)" }, { key: "invested", name: chartLabels.invested, color: NAVY, fill: "url(#gNavy)" }]} />
              </div>
            )}
            {compare && (
              <div className="border border-navy/10 bg-white p-8 shadow-elevated" data-testid="goal-compare">
                <div className="font-serif text-xl text-navy">{compare.title}</div>
                <p className="font-sans text-sm text-slate mt-1">{compare.intro}</p>
                <div className="mt-6 flex flex-col gap-4">
                  {compare.rows.map((r) => (
                    <div key={r.label}>
                      <div className="flex justify-between font-sans text-sm mb-1.5">
                        <span className="text-offblack">{r.label}</span>
                        <span className="font-data text-navy">{r.display || fmtINR(r.amount)}</span>
                      </div>
                      <div className="h-3 bg-cream">
                        <div className={`h-full origin-left ${r.highlight ? "bg-navy" : "bg-orange"}`} style={{ transform: `scaleX(${r.amount / maxCompare})` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <Disclaimer className="mt-6" />
      </Section>

      {/* ALLOCATION */}
      <Section cream>
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionHead label="Right asset · right proportion · right time" title="How the mix may shift for this goal" intro={result.allocationNote || `With about ${result.yearsLeft} years to go, an illustrative starting mix might look like this. As the goal gets closer, equity is reduced to protect what has been built.`} />
          </div>
          <div className="lg:col-span-7 bg-white p-8 md:p-10 shadow-floating border-t-2 border-citrine">
            <div className="flex h-10 gap-0.5">
              <div className="bg-navy" style={{ flex: alloc.equity }} />
              <div className="bg-slate/60" style={{ flex: alloc.debt }} />
              <div className="bg-citrine" style={{ flex: alloc.gold }} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 font-sans text-sm">
              <div><span className="font-data text-2xl text-navy block">{alloc.equity}%</span>Equity</div>
              <div><span className="font-data text-2xl text-navy block">{alloc.debt}%</span>Debt &amp; Bonds</div>
              <div><span className="font-data text-2xl text-navy block">{alloc.gold}%</span>Gold</div>
            </div>
            {!result.allocation && (
              <>
            <div className="mt-8 pt-6 border-t border-navy/10 grid grid-cols-5 gap-2 text-center">
              {[10, 7, 5, 3, 1].map((y) => {
                const a = glideAllocation(y);
                return (
                  <div key={y}>
                    <div className="h-24 bg-cream flex flex-col justify-end">
                      <div className="bg-navy" style={{ height: `${a.equity}%` }} />
                    </div>
                    <div className="font-data text-xs text-slate mt-2">{y}{y === 10 ? "+" : ""} yrs</div>
                  </div>
                );
              })}
            </div>
            <p className="font-data text-[11px] text-slate mt-4">Equity share by years left to goal. Illustrative only; your actual allocation is set after a risk assessment.</p>
              </>
            )}
          </div>
        </div>
      </Section>

      {/* EBOOK */}
      <Section dark id="guide">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 flex justify-center lg:justify-start">
            <EbookCover goal={goal} />
          </div>
          <div className="lg:col-span-4">
            <SectionLabel dark>Free guide</SectionLabel>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl tracking-[-0.03em] leading-[1.08]">{goal.ebook.title}</h2>
            <p className="mt-4 font-sans text-white/70 leading-[1.7]">{goal.ebook.subtitle}. Six short chapters, written in plain language.</p>
            <ol className="mt-8 flex flex-col gap-3">
              {goal.ebook.chapters.map((c, i) => (
                <li key={c} className="flex gap-4 border-t border-white/10 pt-3 font-sans text-sm text-white/85">
                  <span className="font-data text-citrine">{String(i + 1).padStart(2, "0")}</span>
                  {c}
                </li>
              ))}
            </ol>
          </div>
          <div className="lg:col-span-4 bg-white/[0.04] border border-white/10 p-8 shadow-floating">
            {ebookUnlocked ? (
              <div className="flex flex-col gap-4" data-testid="ebook-ready">
                <CheckCircle2 className="w-10 h-10 text-citrine" strokeWidth={1.5} />
                <div className="font-serif text-2xl">Your guide is ready</div>
                <p className="font-sans text-sm text-white/65">It opened in a new tab, and a copy is on its way to your email.</p>
                <a href={goal.ebook.file} target="_blank" rel="noopener noreferrer" className={`${btn} self-start bg-orange text-white hover:bg-orange-dark`}>
                  <Download className="w-4 h-4" strokeWidth={1.5} /> Open the guide
                </a>
              </div>
            ) : (
              <div>
                <div className="font-serif text-2xl mb-1">Get the free guide</div>
                <p className="font-sans text-sm text-white/65 mb-5">Instant download, plus a copy by email.</p>
                <GateForm goal={goal} magnet="E-book" result={result} cta="Send me the guide" onUnlocked={deliverEbook} dark testid="ebook-gate" />
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><SectionHead label="Questions" title="What investors ask us" /></div>
          <div className="lg:col-span-8"><FAQ items={goal.faq} /></div>
        </div>
      </Section>

      {/* TRUST + CTA */}
      <section className="relative z-10 bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <SectionLabel dark>Talk it through</SectionLabel>
            <h2 className="mt-4 font-serif text-4xl lg:text-6xl tracking-[-0.03em] leading-[1.05]">A calculator gives a number. <span className="text-citrine">A plan gives you a path.</span></h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className={`${btn} bg-orange text-white hover:bg-orange-dark hover:shadow-citrine`} data-testid="goal-cta-call">
                Book a free 20-minute goal call
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <a href="https://wa.me/917506784004" target="_blank" rel="noopener noreferrer" className={`${btn} border border-citrine/60 text-white hover:bg-white hover:text-navy`} data-testid="goal-cta-whatsapp">
                WhatsApp us
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-6">
            {[["ARN-261755", "AMFI-registered MFD"], ["9+ yrs", "In financial services"], ["150+", "Investors served"], ["₹50 Cr+", "Assets advised"]].map(([v, l]) => (
              <div key={l} className="border-t border-citrine/50 pt-4">
                <div className="font-data text-2xl">{v}</div>
                <div className="font-sans text-xs uppercase tracking-[0.16em] text-white/60 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER GOALS */}
      <Section cream>
        <SectionHead label="Other goals" title="Planning for something else?" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {others.map((g) => (
            <Link key={g.slug} to={`/goals/${g.slug}`} className="group bg-white p-8 shadow-elevated border border-navy/5 hover:shadow-floating focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-citrine transition-shadow duration-300" data-testid={`goal-link-${g.slug}`}>
              <div className="font-sans text-xs uppercase tracking-[0.2em] text-slate">{g.hero.label}</div>
              <div className="mt-3 font-serif text-2xl text-navy">{g.name}</div>
              <div className="mt-6 inline-flex items-center gap-2 font-sans text-sm text-orange">Plan this goal <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} /></div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

function EbookCover({ goal }) {
  return (
    <div className="relative [perspective:1200px]">
      <img
        src={goal.ebook.cover}
        alt={`${goal.ebook.title} — free guide cover`}
        width="420"
        height="594"
        className="w-[260px] md:w-[320px] h-auto shadow-floating [transform:rotateY(-14deg)_rotateX(4deg)] transition-transform duration-500 hover:[transform:rotateY(-4deg)]"
        loading="lazy"
      />
      <div aria-hidden="true" className="absolute -bottom-6 left-6 right-0 h-8 bg-navy-deep/20 blur-xl" />
    </div>
  );
}
